import React, { useState, useCallback, useEffect, useRef } from 'react';

import { Flex, Image, useTheme, VStack } from '@chakra-ui/react';
import { CreateNewTag } from 'components/RabbitOKRSidePanel/Components/CreateNewTag';
import SearchBar from 'components/RabbitOKRSidePanel/Components/SearchBar';
import {
  allowedCharactersRegex,
  TAG_MAX_LENGTH,
} from 'components/RabbitOKRSidePanel/SecondaryPanel/AddEditCustomTagsPanel';
import {
  useCustomTagsQuery,
  useAttachTagToOKRMutation,
  useModifyTagToOKRMutation,
} from 'components/RabbitOKRSidePanel/datahooks/useGetCustomTagsOrganization';
import {
  SECONDARY_PANEL_TYPES,
  openOKRAddEditCustomTagsPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';
import { Divider1 } from 'styles/common';

import { Plus } from 'assets/assetsV1/iconComponents';
import NO_TAG_IMAGE from 'assets/assetsV1/images/notagIcon.svg';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { TextBodyMedium, TextHeader2 } from 'uiToolkitV1/Typography';

import CustomTagChipList from './CustomTagChipList';
import CustomTagWithoutLabel from './CustomTagWithoutLabel';

export default function TagSearchAndSelect({
  curSelectedTags = [],
  onClose = () => {},
  onSave = () => {},
  okrId,
  isOpen,
  canCreateEditViewDeleteAssignCustomTags = false,
  showNewTagCreation,
  isTagEditionAsChild = false,
  isTagEditionInSidePanelChildCreation = false,
  isSendUpdatedTagsToParent = false,
  isNewlyCreatedOkr = false,
}) {
  const [searchText, setSearchText] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [displayTags, setDisplayTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const firstUpdate = useRef(true);
  const theme = useTheme();
  const dispatch = useDispatch();
  const toast = useToast();
  const okrCreationDetails = useSelector(okrCreationPanelStateSelector);

  const resetComponent = () => {
    setSearchText('');
    setSelectedTags(curSelectedTags);
  };

  useEffect(() => {
    setSelectedTags(curSelectedTags);
  }, [curSelectedTags]);

  useEffect(() => {
    if (isOpen === false) {
      resetComponent();
    }
  }, [isOpen]);

  const { error: customTagsError, data: customTagsData } = useCustomTagsQuery();

  const { mutate: attachTagToOKR } = useAttachTagToOKRMutation(onSave);
  const { mutate: modifyTagToOKR } = useModifyTagToOKRMutation(onSave);
  const isTagChecked = (tag) => Boolean(selectedTags.find((selectedTag) => selectedTag.uuid === tag.uuid));

  const isModifiedCallback = useCallback(() => {
    if (selectedTags.length !== curSelectedTags.length) {
      return true;
    }

    let isModifiedFlag = false;
    const curSelectedTagMap = {};
    curSelectedTags.forEach((tag) => {
      curSelectedTagMap[tag.uuid] = true;
    });
    selectedTags.forEach((tag) => {
      if (!curSelectedTagMap[tag.uuid] || (curSelectedTagMap[tag.uuid] && !isTagChecked(tag))) {
        isModifiedFlag = true;
      }
    });
    return isModifiedFlag;
  }, [curSelectedTags, isTagChecked, selectedTags]);

  const isTagAlreadyAttached = (tag) => Boolean(curSelectedTags.find((curTag) => curTag.uuid === tag.uuid));

  const handleTagUpdate = () => {
    if (isNewlyCreatedOkr || isSendUpdatedTagsToParent) {
      onSave(selectedTags);
      return;
    }
    const newlyAttachedTags = selectedTags.filter((tag) => !isTagAlreadyAttached(tag));
    const deletedTags = curSelectedTags.filter((tag) => !isTagChecked(tag));
    newlyAttachedTags.forEach((tag) => {
      attachTagToOKR({ okrId, tagKeyId: tag?.uuid });
    });
    deletedTags.forEach((tag) => {
      modifyTagToOKR({ okrId, tagKeyId: tag?.uuid });
    });
  };

  useEffect(() => {
    if (customTagsError) {
      toast({
        title: 'Failed to fetch tags',
        status: TOAST_STATUSES.ERROR,
      });
      return;
    }
    if (customTagsData) {
      setAllTags([...customTagsData]);
      setDisplayTags([...customTagsData]);
    }
  }, [customTagsData, customTagsError]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (searchText) {
      setDisplayTags(allTags.filter((tag) => tag?.tagName?.toLowerCase().includes(searchText.toLowerCase())));
    } else {
      setDisplayTags([...allTags]);
    }
  }, [searchText, isOpen]);

  const handleInputChange = useCallback((e) => setSearchText(e.target.value), []);
  const handleClearInput = useCallback(() => {
    setSearchText('');
  }, []);

  const onCheckboxChange = (event, selectedTag) => {
    const isSelected = event.target.checked;
    if (isSelected) {
      setSelectedTags([...selectedTags, { ...selectedTag }]);
    } else {
      setSelectedTags(selectedTags.filter((tag) => tag.uuid !== selectedTag.uuid));
    }
  };

  const [newTagError, setNewTagError] = useState('');

  useEffect(() => {
    if (searchText?.length > TAG_MAX_LENGTH) {
      setNewTagError('Tag name cannot exceed 200 characters.');
    } else if (!allowedCharactersRegex.test(searchText)) {
      setNewTagError('Special characters not allowed in the tag name');
    } else {
      setNewTagError('');
    }
  }, [searchText]);

  if (allTags.length === 0) {
    return (
      <Flex direction="column" bg="red.200" height="100%" overflow="hidden">
        <VStack direction="column" align="center" spacing="10px" minH="220px" padding="32px 0 16px 0">
          <Flex>
            <Image src={NO_TAG_IMAGE} width="48px" height="80px" />
          </Flex>
          <Flex>
            <TextHeader2 bold>No tags to show</TextHeader2>
          </Flex>
          <Flex>
            <TextBodyMedium>Add your first tag to select</TextBodyMedium>
          </Flex>
          <Flex>
            <Button
              variant="outlined"
              StartIcon={Plus}
              color="blue"
              onClick={() => {
                onClose();
                dispatch(openOKRAddEditCustomTagsPanel({ okrId, onSave }));
              }}
            >
              Add tag
            </Button>
          </Flex>
        </VStack>
      </Flex>
    );
  }

  return (
    <Flex direction="column" height="100%" overflow="hidden">
      <Flex direction="column" px="10px" flexGrow={1} minHeight={0}>
        <Flex mt="15px">
          <SearchBar
            value={searchText}
            placeholder="Search tags"
            onChange={handleInputChange}
            onClearInput={handleClearInput}
          />
          {canCreateEditViewDeleteAssignCustomTags && (
            <Button
              variant="tertiary"
              color="blue"
              onClick={() => {
                onClose();
                dispatch(
                  openOKRAddEditCustomTagsPanel({
                    okrId,
                    onSave,
                    isOpenAsChild: isTagEditionAsChild,
                    isTagEditionInSidePanelChildCreation,

                    okrDetails:
                      okrId && !isTagEditionInSidePanelChildCreation
                        ? {
                            type: SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL,
                          }
                        : okrCreationDetails,
                  })
                );
              }}
            >
              Edit Tags
            </Button>
          )}
        </Flex>

        <Flex mb="5px">
          {selectedTags.length > 0 ? (
            <Flex align="center" sx={{ textWrap: 'nowrap' }}>
              <TextBodyMedium color="text.g1">Selected ({selectedTags.length}):</TextBodyMedium>
            </Flex>
          ) : null}
          <CustomTagChipList
            tags={selectedTags}
            tagsLimit={isMobileView() ? 1 : 3}
            isShowCloseButton
            containerProps={{ marginLeft: '10px', wrap: 'wrap' }}
            onTagClose={(selectedTag) => onCheckboxChange({ target: { checked: false } }, selectedTag)}
          />
        </Flex>

        <Divider1 />

        <Flex
          direction="column"
          overflow="scroll"
          flexGrow={1}
          id="selected-tags-list"
          height={isMobileView() ? 'auto' : '250px'}
        >
          {displayTags?.length > 0
            ? displayTags.map((tag) => (
                <Flex justify="space-between" padding="12px 16px 12px 16px">
                  <Flex width="calc(100% - 54px)" overflow="scroll">
                    <CustomTagWithoutLabel key={tag.uuid} tag={tag} />
                  </Flex>
                  <Checkbox
                    isChecked={isTagChecked(tag)}
                    onChange={(event) => onCheckboxChange(event, tag)}
                    height="28px"
                  />
                </Flex>
              ))
            : showNewTagCreation &&
              canCreateEditViewDeleteAssignCustomTags && (
                <CreateNewTag
                  okrId={okrId}
                  onClose={onClose}
                  onSave={(newtag) => {
                    setSelectedTags((prev) => [...prev, newtag]);
                    handleClearInput();
                  }}
                  newTagError={newTagError}
                  searchText={searchText}
                  showCreationScreen={showNewTagCreation && canCreateEditViewDeleteAssignCustomTags}
                />
              )}
        </Flex>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" padding="12px 16px" background="#F5F7F9">
        <Button variant="tertiary" mr="auto" onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="filled"
          colorScheme="blue"
          onClick={() => handleTagUpdate()}
          disabled={!isModifiedCallback()}
          mr="10px"
        >
          Apply
        </Button>
      </Flex>
    </Flex>
  );
}
